// Templates library - mission template CRUD, task items, assignments, scheduling
// CHANGE: Removed all debug console.log/warn/error statements for production
// UPDATED: Added propagateTemplateItemsToActiveAssignments for syncing items to active missions
// UPDATED: propagateTemplateItemsToActiveAssignments now uses replace_assigned_task_items RPC for atomic delete+insert per task
// UPDATED: Added propagateTemplateMetadataToActiveAssignments for syncing metadata (name, service, duration) to active missions
// UPDATED: assignTaskToUser now snapshots template metadata (name, description, duration, service) at assignment time
// UPDATED: propagateTemplateItemsToActiveAssignments now uses Promise.allSettled for parallel RPC calls instead of sequential loop
// UPDATED: Added text_input_label to createTemplateWithItems, createTaskItem, updateTaskItem, duplicateTemplate, and assignTaskToUser payloads
// UPDATED: checkSchedulingConflict now expands recurring tasks via getTaskOccurrences to detect overlaps on the target date
// UPDATED: Empty catch in checkSchedulingConflict now logs console.error with task id and error for visibility

import { supabase } from './supabase';
import type { TaskTemplate, TaskItem, RecurrenceRule, AssignedTaskOccurrence } from './supabase';
import { parseDate, calculateEndTime } from './dateUtils';
import { getTaskOccurrences, type CalculatedOccurrence } from './occurrences';
import type { AssignedTaskWithDetails } from './execution';
import type { TaskStatus } from './statusUtils';
import { addBreadcrumb, captureException } from './sentry';

interface RawTemplateTaskWithJoins {
  id: string;
  assigned_to_user_id: string;
  site_id: string;
  template_id: string;
  assigned_date: string;
  scheduled_time: string | null;
  status: TaskStatus;
  completed_at: string | null;
  is_recurring: boolean;
  duration_override: number | null;
  recurrence_end_date: string | null;
  template_name: string | null;
  template_description: string | null;
  template_estimated_duration_minutes: number | null;
  template_service_id: string | null;
  user: { id: string; username: string; full_name: string } | null;
  site: { id: string; name: string } | null;
  recurrence?: RecurrenceRule | RecurrenceRule[];
}

export interface TemplateWithItems extends TaskTemplate {
  items?: TaskItem[];
  itemCount?: number;
  recurrence?: RecurrenceRule | null;
  service?: {
    id: string;
    name: string;
    color: string;
  } | null;
}

export interface CreateTemplateData {
  name: string;
  description?: string;
  estimated_duration_minutes?: number;
  service_id?: string | null;
}

export interface UpdateTemplateData {
  name?: string;
  description?: string;
  estimated_duration_minutes?: number;
  service_id?: string | null;
}

// Get all templates (optionally include archived)
export async function getTemplates(includeArchived = false): Promise<TemplateWithItems[]> {
  try {
    const query = supabase
      .from('task_templates')
      .select(`
        *,
        items:task_items(count),
        recurrence:recurrence_rules(*),
        service:services(id, name, color)
      `)
      .eq('is_archived', includeArchived)
      .order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    // Transform the count aggregate into itemCount and extract recurrence
    const templates = (data || []).map(template => ({
      ...template,
      itemCount: template.items?.[0]?.count || 0,
      recurrence: template.recurrence?.[0] || null,
      items: undefined // Remove the items aggregate field
    }));

    return templates;
  } catch (error) {
    throw error;
  }
}

// Get a single template by ID
export async function getTemplate(templateId: string): Promise<TaskTemplate | null> {
  try {
    const { data, error } = await supabase
      .from('task_templates')
      .select('*')
      .eq('id', templateId)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    throw error;
  }
}

// Get a template with all its items
export async function getTemplateWithItems(templateId: string): Promise<TemplateWithItems | null> {
  try {
    const { data: template, error: templateError } = await supabase
      .from('task_templates')
      .select('*')
      .eq('id', templateId)
      .maybeSingle();

    if (templateError) {
      throw templateError;
    }

    if (!template) {
      return null;
    }

    const { data: items, error: itemsError } = await supabase
      .from('task_items')
      .select('*')
      .eq('template_id', templateId)
      .order('order_index', { ascending: true });

    if (itemsError) {
      throw itemsError;
    }

    // Get recurrence rule if exists
    const { data: recurrenceData, error: recurrenceError } = await supabase
      .from('recurrence_rules')
      .select('*')
      .eq('template_id', templateId)
      .maybeSingle();

    if (recurrenceError) {
    }

    return {
      ...template,
      items: items || [],
      itemCount: items?.length || 0,
      recurrence: recurrenceData || null
    };
  } catch (error) {
    throw error;
  }
}

// Create a new template
export async function createTemplate(data: CreateTemplateData): Promise<TaskTemplate> {
  addBreadcrumb('template', 'Creation de modele de mission', { name: data.name });
  try {
    const { data: template, error } = await supabase
      .from('task_templates')
      .insert({
        name: data.name,
        description: data.description || null,
        estimated_duration_minutes: data.estimated_duration_minutes || null,
        service_id: data.service_id || null,
        is_archived: false
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    return template;
  } catch (error) {
    throw error;
  }
}

// Create a new template with items in one operation
export async function createTemplateWithItems(
  data: CreateTemplateData,
  items: TaskItem[]
): Promise<TaskTemplate> {
  try {
    // Create the template first
    const template = await createTemplate(data);

    // Create all items if any
    if (items && items.length > 0) {
      const itemsToInsert = items.map((item, index) => ({
        template_id: template.id,
        title: item.title,
        order_index: index,
        // Text input fields
        requires_text_input: item.requires_text_input || false,
        text_input_label: item.text_input_label || null,
        text_input_placeholder: item.text_input_placeholder || null,
        is_text_input_required: item.is_text_input_required || false,
      }));

      const { error: itemsError } = await supabase
        .from('task_items')
        .insert(itemsToInsert);

      if (itemsError) {
        throw itemsError;
      }
    }

    return template;
  } catch (error) {
    throw error;
  }
}

// Update an existing template
export async function updateTemplate(
  templateId: string,
  data: UpdateTemplateData
): Promise<TaskTemplate> {
  addBreadcrumb('template', 'Modification de modele de mission', { templateId });
  try {
    const updateData: any = { updated_at: new Date().toISOString() };

    if (data.name !== undefined) updateData.name = data.name;
    if (data.description !== undefined) updateData.description = data.description || null;
    if (data.estimated_duration_minutes !== undefined) {
      updateData.estimated_duration_minutes = data.estimated_duration_minutes || null;
    }
    if (data.service_id !== undefined) updateData.service_id = data.service_id || null;

    const { data: template, error } = await supabase
      .from('task_templates')
      .update(updateData)
      .eq('id', templateId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return template;
  } catch (error) {
    throw error;
  }
}

// Archive a template (soft delete)
export async function archiveTemplate(templateId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('task_templates')
      .update({ is_archived: true, updated_at: new Date().toISOString() })
      .eq('id', templateId);

    if (error) {
      throw error;
    }
  } catch (error) {
    throw error;
  }
}

// Restore an archived template
export async function restoreTemplate(templateId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('task_templates')
      .update({ is_archived: false, updated_at: new Date().toISOString() })
      .eq('id', templateId);

    if (error) {
      throw error;
    }
  } catch (error) {
    throw error;
  }
}

// Duplicate a template (with all its items)
export async function duplicateTemplate(templateId: string): Promise<TaskTemplate> {
  try {
    // Get the original template with items and recurrence
    const original = await getTemplateWithItems(templateId);
    if (!original) {
      throw new Error('Template not found');
    }

    // Create the new template
    const newTemplate = await createTemplate({
      name: `${original.name} (Copie)`,
      description: original.description || undefined,
      estimated_duration_minutes: original.estimated_duration_minutes || undefined,
      service_id: original.service_id || undefined
    });

    // Copy all items with all their configuration if any
    if (original.items && original.items.length > 0) {
      const itemsToInsert = original.items.map(item => ({
        template_id: newTemplate.id,
        title: item.title,
        order_index: item.order_index,
        // Text input fields
        requires_text_input: item.requires_text_input || false,
        text_input_label: item.text_input_label || null,
        text_input_placeholder: item.text_input_placeholder || null,
        is_text_input_required: item.is_text_input_required || false,
      }));

      const { error: itemsError } = await supabase
        .from('task_items')
        .insert(itemsToInsert);

      if (itemsError) {
        throw itemsError;
      }
    }

    // Copy recurrence rule if exists
    if (original.recurrence) {
      const recurrenceToInsert = {
        template_id: newTemplate.id,
        frequency: original.recurrence.frequency,
        interval: original.recurrence.interval,
        days_of_week: original.recurrence.days_of_week,
        day_of_month: original.recurrence.day_of_month,
        month_of_year: original.recurrence.month_of_year,
        week_of_month: original.recurrence.week_of_month
      };

      const { error: recurrenceError } = await supabase
        .from('recurrence_rules')
        .insert(recurrenceToInsert);

      if (recurrenceError) {
        // Don't throw - recurrence is optional, continue with template duplication
      }
    }

    return newTemplate;
  } catch (error) {
    throw error;
  }
}

// Delete a template permanently (use with caution)
export async function deleteTemplate(templateId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('task_templates')
      .delete()
      .eq('id', templateId);

    if (error) {
      throw error;
    }
  } catch (error) {
    throw error;
  }
}

// Get items count for a template
export async function getTemplateItemsCount(templateId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('task_items')
      .select('*', { count: 'exact', head: true })
      .eq('template_id', templateId);

    if (error) {
      throw error;
    }

    return count || 0;
  } catch (error) {
    throw error;
  }
}

// ===== TASK ITEMS CRUD OPERATIONS =====

// Create a new task item with all optional fields
export async function createTaskItem(
  templateId: string,
  title: string,
  orderIndex: number,
  options?: Partial<TaskItem>
): Promise<TaskItem> {
  try {
    const itemData: any = {
      template_id: templateId,
      title,
      order_index: orderIndex,
      // Text input fields
      requires_text_input: options?.requires_text_input || false,
      text_input_label: options?.text_input_label || null,
      text_input_placeholder: options?.text_input_placeholder || null,
      is_text_input_required: options?.is_text_input_required || false,
      // KPI fields
      text_input_type: options?.text_input_type || 'text',
      kpi_definition_id: options?.kpi_definition_id ?? null,
    };

    const { data, error } = await supabase
      .from('task_items')
      .insert(itemData)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    throw error;
  }
}

// Update a task item with all possible fields
export async function updateTaskItem(itemId: string, updates: Partial<TaskItem>): Promise<TaskItem> {
  try {
    const updateData: any = {};

    // Only include fields that are provided in updates
    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.requires_text_input !== undefined) updateData.requires_text_input = updates.requires_text_input;
    if (updates.text_input_label !== undefined) updateData.text_input_label = updates.text_input_label;
    if (updates.text_input_placeholder !== undefined) updateData.text_input_placeholder = updates.text_input_placeholder;
    if (updates.is_text_input_required !== undefined) updateData.is_text_input_required = updates.is_text_input_required;
    if (updates.text_input_type !== undefined) updateData.text_input_type = updates.text_input_type;
    if ('kpi_definition_id' in updates) updateData.kpi_definition_id = updates.kpi_definition_id ?? null;

    const { data, error } = await supabase
      .from('task_items')
      .update(updateData)
      .eq('id', itemId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    throw error;
  }
}

// Delete a task item
export async function deleteTaskItem(itemId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('task_items')
      .delete()
      .eq('id', itemId);

    if (error) {
      throw error;
    }
  } catch (error) {
    throw error;
  }
}

// Reorder task items (update order_index for multiple items)
export async function reorderTaskItems(items: { id: string; order_index: number }[]): Promise<void> {
  try {
    // Update each item's order_index
    const updates = items.map(item =>
      supabase
        .from('task_items')
        .update({ order_index: item.order_index })
        .eq('id', item.id)
    );

    const results = await Promise.all(updates);

    // Check for errors
    const errors = results.filter(r => r.error);
    if (errors.length > 0) {
      throw new Error('Failed to reorder some items');
    }
  } catch (error) {
    throw error;
  }
}

// ============================================================================
// TASK ASSIGNMENT - Phase 2 Task 4
// ============================================================================

export interface AssignTaskData {
  userId: string;
  siteId: string;
  date: string; // Format: YYYY-MM-DD
  time?: string; // Format: HH:MM (optional)
  durationOverride?: number; // Optional duration override in minutes
  // Recurrence fields
  isRecurring?: boolean;
  recurrenceRuleId?: string;
  recurrenceEndDate?: string; // Format: YYYY-MM-DD (optional)
}

export interface TimeSlotConflict {
  hasConflict: boolean;
  conflictingTask?: {
    id: string;
    title: string;
    startTime: string;
    endTime: string;
    duration: number;
  };
}

// Helper function to check if two time ranges overlap
function timeRangesOverlap(
  start1: string,
  end1: string,
  start2: string,
  end2: string
): boolean {
  // Convert times to minutes for easier comparison
  const toMinutes = (time: string) => {
    const [hours, mins] = time.split(':').map(Number);
    return hours * 60 + mins;
  };

  const s1 = toMinutes(start1);
  const e1 = toMinutes(end1);
  const s2 = toMinutes(start2);
  const e2 = toMinutes(end2);

  // Two ranges overlap if one starts before the other ends
  return s1 < e2 && s2 < e1;
}

// Check for scheduling conflicts before assigning a task
export async function checkSchedulingConflict(
  userId: string,
  date: string,
  startTime: string,
  duration: number
): Promise<TimeSlotConflict> {
  try {
    const endTime = calculateEndTime(startTime, duration);

    // Check direct (non-recurring) tasks on this exact date
    const { data: assignedTasks, error } = await supabase
      .from('assigned_tasks')
      .select(`
        id,
        scheduled_time,
        duration_override,
        status,
        template_name,
        template_estimated_duration_minutes
      `)
      .eq('assigned_to_user_id', userId)
      .eq('assigned_date', date)
      .eq('status', 'not_started');

    if (error) {
      throw error;
    }

    for (const task of (assignedTasks || [])) {
      if (!task.scheduled_time) {
        continue;
      }

      const taskDuration = (task as any).duration_override || (task as any).template_estimated_duration_minutes || 30;
      const taskEndTime = calculateEndTime(task.scheduled_time, taskDuration);

      if (timeRangesOverlap(startTime, endTime, task.scheduled_time, taskEndTime)) {
        return {
          hasConflict: true,
          conflictingTask: {
            id: task.id,
            title: (task as any).template_name || 'Mission',
            startTime: task.scheduled_time,
            endTime: taskEndTime,
            duration: taskDuration
          }
        };
      }
    }

    // Check recurring tasks whose recurrence pattern could produce an occurrence on this date
    const { data: recurringTasks, error: recurringError } = await supabase
      .from('assigned_tasks')
      .select(`
        id,
        assigned_to_user_id,
        site_id,
        template_id,
        assigned_date,
        scheduled_time,
        duration_override,
        is_recurring,
        recurrence_rule_id,
        recurrence_start_date,
        recurrence_end_date,
        reassignment_from_date,
        reassignment_user_id,
        reassignment_scheduled_time,
        template_name,
        template_estimated_duration_minutes,
        recurrence:recurrence_rules!assigned_tasks_recurrence_rule_id_fkey (*)
      `)
      .eq('assigned_to_user_id', userId)
      .eq('is_recurring', true)
      .eq('status', 'not_started')
      .lte('recurrence_start_date', date)
      .or(`recurrence_end_date.is.null,recurrence_end_date.gte.${date}`);

    if (recurringError) {
      throw recurringError;
    }

    const targetDate = parseDate(date);

    for (const task of (recurringTasks || [])) {
      const rule = (task as any).recurrence;
      if (!rule) continue;

      let occurrences: CalculatedOccurrence[];
      try {
        occurrences = await getTaskOccurrences(
          task as Parameters<typeof getTaskOccurrences>[0],
          rule as Parameters<typeof getTaskOccurrences>[1],
          { startDate: targetDate, endDate: targetDate }
        );
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('[templates] getTaskOccurrences failed for task', task.id, 'on date', targetDate, err);
        }
        captureException(err, { taskId: task.id, targetDate });
        throw err;
      }

      for (const occurrence of occurrences) {
        if (
          occurrence.date !== date ||
          occurrence.assignedUserId !== userId ||
          occurrence.isCancelled ||
          !occurrence.scheduledTime
        ) {
          continue;
        }

        const occDuration = occurrence.duration || (task as any).template_estimated_duration_minutes || 30;
        const occEndTime = calculateEndTime(occurrence.scheduledTime, occDuration);

        if (timeRangesOverlap(startTime, endTime, occurrence.scheduledTime, occEndTime)) {
          return {
            hasConflict: true,
            conflictingTask: {
              id: task.id,
              title: (task as any).template_name || 'Mission',
              startTime: occurrence.scheduledTime,
              endTime: occEndTime,
              duration: occDuration
            }
          };
        }
      }
    }

    return { hasConflict: false };
  } catch (error) {
    throw error;
  }
}

// Assign a template to a user (create an assigned_task with all its items)
export async function assignTaskToUser(
  templateId: string,
  data: AssignTaskData
): Promise<string> {
  addBreadcrumb('template', 'Attribution de mission a un utilisateur', { templateId, userId: data.userId });
  try {
    // 1. Get the template with all its items
    const template = await getTemplateWithItems(templateId);
    if (!template) {
      throw new Error('Template not found');
    }

    if (!template.items || template.items.length === 0) {
      throw new Error('Template has no items');
    }

    // 2. Check for scheduling conflicts if time is specified
    if (data.time) {
      const duration = data.durationOverride || template.estimated_duration_minutes || 30;
      const conflict = await checkSchedulingConflict(
        data.userId,
        data.date,
        data.time,
        duration
      );

      if (conflict.hasConflict && conflict.conflictingTask) {
        const error = new Error('Scheduling conflict detected');
        (error as any).conflictingTask = conflict.conflictingTask;
        throw error;
      }
    }

    // 3. Create the assigned_task
    const insertData = {
      template_id: templateId,
      assigned_to_user_id: data.userId,
      site_id: data.siteId,
      assigned_date: data.date,
      scheduled_time: data.time || null,
      duration_override: data.durationOverride || null,
      status: 'not_started',
      // Snapshot of template metadata at assignment time
      template_name: template.name,
      template_description: template.description || null,
      template_estimated_duration_minutes: template.estimated_duration_minutes,
      template_service_id: template.service_id || null,
      // Recurrence fields
      is_recurring: data.isRecurring || false,
      recurrence_rule_id: data.recurrenceRuleId || null,
      recurrence_start_date: data.isRecurring ? data.date : null,
      recurrence_end_date: data.recurrenceEndDate || null,
      parent_task_id: null
    };

    const { data: assignedTask, error: assignError } = await supabase
      .from('assigned_tasks')
      .insert(insertData)
      .select()
      .single();

    if (assignError) {
      throw assignError;
    }

    // 4. Copy all task_items to assigned_task_items with all metadata
    const itemsToInsert = template.items.map(item => ({
      assigned_task_id: assignedTask.id,
      // Note: task_item_id and description will be added after migration
      // See: supabase/migrations/MANUAL_20251113120000_add_missing_fields_to_assigned_task_items.sql
      title: item.title,
      order_index: item.order_index,
      is_completed: false,
      // Copy text input configuration
      requires_text_input: item.requires_text_input || false,
      text_input_label: item.text_input_label || null,
      text_input_placeholder: item.text_input_placeholder || null,
      is_text_input_required: item.is_text_input_required || false,
      // KPI fields
      text_input_type: item.text_input_type || 'text',
      kpi_definition_id: item.kpi_definition_id ?? null
    }));

    const { error: itemsError } = await supabase
      .from('assigned_task_items')
      .insert(itemsToInsert);

    if (itemsError) {
      // Rollback: delete the assigned_task if items creation failed
      await supabase.from('assigned_tasks').delete().eq('id', assignedTask.id);
      throw itemsError;
    }

    return assignedTask.id;
  } catch (error) {
    throw error;
  }
}

export interface GetAssignedTasksFilters {
  userId?: string;
  siteId?: string;
  status?: 'not_started' | 'completed' | 'overdue';
  dateFrom?: string; // Format: YYYY-MM-DD
  dateTo?: string; // Format: YYYY-MM-DD
}

// Helper: Create a virtual occurrence task from a recurring task and calculated occurrence
function createVirtualOccurrenceForTemplate(
  parentTask: RawTemplateTaskWithJoins,
  occurrence: CalculatedOccurrence,
  occurrenceRecord: AssignedTaskOccurrence | null
): AssignedTaskWithDetails {
  const virtualId = `${parentTask.id}_${occurrence.date}`;

  return {
    id: virtualId,
    assigned_to_user_id: parentTask.assigned_to_user_id,
    site_id: parentTask.site_id,
    template_id: parentTask.template_id,
    assigned_date: occurrence.date,
    scheduled_time: occurrence.scheduledTime || parentTask.scheduled_time,
    status: occurrenceRecord?.status !== null && occurrenceRecord?.status !== undefined
      ? occurrenceRecord.status
      : occurrence.status,
    completed_at: null,
    is_recurring: true,
    recurrence_pattern: null,
    duration_override_minutes: occurrence.duration || parentTask.duration_override,
    template_service_id: parentTask.template_service_id || null,
    template: {
      id: parentTask.template_id,
      name: parentTask.template_name || '',
      description: parentTask.template_description || null,
      estimated_duration_minutes: parentTask.template_estimated_duration_minutes || 0,
      service_id: parentTask.template_service_id || null
    },
    site: parentTask.site,
    user: parentTask.user,
    isVirtualOccurrence: true,
    parentTaskId: parentTask.id,
    occurrenceDate: occurrence.date,
    occurrenceData: occurrenceRecord || undefined
  } as AssignedTaskWithDetails;
}

// Get assigned tasks with optional filters
// UPDATED: Now includes recurring task occurrences calculated for the date range
export async function getAssignedTasks(
  filters?: GetAssignedTasksFilters
): Promise<AssignedTaskWithDetails[]> {
  try {
    // If we have date filters, we need to handle recurring tasks
    const hasDateFilters = filters?.dateFrom || filters?.dateTo;
    const targetDate = filters?.dateFrom || filters?.dateTo;

    // Query for direct (non-recurring) tasks
    let directQuery = supabase
      .from('assigned_tasks')
      .select(`
        *,
        user:users!assigned_tasks_assigned_to_user_id_fkey (
          id,
          username,
          full_name
        ),
        site:sites (
          id,
          name
        )
      `)
      .eq('is_recurring', false)
      .order('assigned_date', { ascending: false })
      .order('created_at', { ascending: false });

    // Apply filters for direct tasks
    if (filters?.userId) {
      directQuery = directQuery.eq('assigned_to_user_id', filters.userId);
    }
    if (filters?.siteId) {
      directQuery = directQuery.eq('site_id', filters.siteId);
    }
    if (filters?.status) {
      directQuery = directQuery.eq('status', filters.status);
    }
    if (filters?.dateFrom) {
      directQuery = directQuery.gte('assigned_date', filters.dateFrom);
    }
    if (filters?.dateTo) {
      directQuery = directQuery.lte('assigned_date', filters.dateTo);
    }

    const { data: directTasks, error: directError } = await directQuery;

    if (directError) {
      throw directError;
    }

    // If we have date filters, also get recurring tasks and calculate occurrences
    let virtualOccurrences: AssignedTaskWithDetails[] = [];

    if (hasDateFilters && targetDate) {
      // Query for recurring tasks that could have occurrences in this date range
      let recurringQuery = supabase
        .from('assigned_tasks')
        .select(`
          *,
          user:users!assigned_tasks_assigned_to_user_id_fkey (
            id,
            username,
            full_name
          ),
          site:sites (
            id,
            name
          ),
          recurrence:recurrence_rules!assigned_tasks_recurrence_rule_id_fkey(*)
        `)
        .eq('is_recurring', true)
        .not('recurrence_rule_id', 'is', null)
        .lte('recurrence_start_date', filters.dateTo || targetDate);

      // Apply user and site filters for recurring tasks too
      if (filters?.userId) {
        recurringQuery = recurringQuery.eq('assigned_to_user_id', filters.userId);
      }
      if (filters?.siteId) {
        recurringQuery = recurringQuery.eq('site_id', filters.siteId);
      }

      const { data: recurringTasks, error: recurringError } = await recurringQuery;

      if (recurringError) {
        throw recurringError;
      }

      // Process each recurring task to generate occurrences
      for (const task of recurringTasks || []) {
        // Extract recurrence rule (handle both array and object from Supabase)
        const rule = Array.isArray(task.recurrence) ? task.recurrence[0] : task.recurrence;

        if (!rule) {
          continue;
        }

        // Check if we're past the recurrence end date
        const checkDate = filters?.dateFrom || filters?.dateTo || targetDate;
        if (task.recurrence_end_date && checkDate > task.recurrence_end_date) {
          continue;
        }

        // Calculate occurrences for the date range
        const startDate = parseDate(filters?.dateFrom || targetDate);
        const endDate = parseDate(filters?.dateTo || targetDate);

        const occurrences = await getTaskOccurrences(
          task,
          rule,
          {
            startDate,
            endDate
          }
        );

        // Create virtual tasks for each occurrence
        for (const occurrence of occurrences) {
          if (occurrence.isCancelled) {
            continue;
          }

          // Apply status filter if provided
          if (filters?.status && occurrence.status !== filters.status) {
            continue;
          }

          const virtualTask = createVirtualOccurrenceForTemplate(
            task,
            occurrence,
            occurrence.occurrenceId ? {
              id: occurrence.occurrenceId,
              assigned_task_id: task.id,
              occurrence_date: occurrence.date,
              status: occurrence.status,
              is_cancelled: occurrence.isCancelled,
              scheduled_time_override: occurrence.scheduledTime,
              duration_override: occurrence.duration,
              notes: occurrence.notes,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            } : null
          );

          virtualOccurrences.push(virtualTask);
        }
      }
    }

    const mappedDirectTasks = (directTasks || []).map((task: any) => ({
      ...task,
      duration_override_minutes: task.duration_override,
      template_service_id: task.template_service_id || null,
      template: {
        id: task.template_id,
        name: task.template_name || '',
        description: task.template_description || null,
        estimated_duration_minutes: task.template_estimated_duration_minutes || 0,
        service_id: task.template_service_id || null
      }
    }));

    const allTasks = [...mappedDirectTasks, ...virtualOccurrences];

    return allTasks;
  } catch (error) {
    throw error;
  }
}

// Cancel an assigned task (only if status is 'not_started')
export async function cancelAssignedTask(assignedTaskId: string): Promise<void> {
  try {
    // First, check the current status
    const { data: task, error: fetchError } = await supabase
      .from('assigned_tasks')
      .select('status')
      .eq('id', assignedTaskId)
      .single();

    if (fetchError) {
      throw fetchError;
    }

    if (task.status !== 'not_started') {
      throw new Error('Can only cancel pending tasks');
    }

    // Delete assigned_task_items first (cascade should handle this, but being explicit)
    const { error: itemsError } = await supabase
      .from('assigned_task_items')
      .delete()
      .eq('assigned_task_id', assignedTaskId);

    if (itemsError) {
      throw itemsError;
    }

    // Delete the assigned_task
    const { error: deleteError } = await supabase
      .from('assigned_tasks')
      .delete()
      .eq('id', assignedTaskId);

    if (deleteError) {
      throw deleteError;
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Propagate template metadata changes to all active (non-completed) assigned tasks.
 * Called after template metadata is modified (name, description, duration, service).
 * Updates the snapshot columns in assigned_tasks for all non-completed tasks.
 */
export async function propagateTemplateMetadataToActiveAssignments(templateId: string): Promise<void> {
  const template = await getTemplate(templateId);
  if (!template) return;

  const { error } = await supabase
    .from('assigned_tasks')
    .update({
      template_name: template.name,
      template_description: template.description || null,
      template_estimated_duration_minutes: template.estimated_duration_minutes,
      template_service_id: template.service_id || null
    })
    .eq('template_id', templateId)
    .neq('status', 'completed');

  if (error) throw error;
}

// Get count of active assigned tasks for a template
export async function getAssignedTasksCount(templateId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('assigned_tasks')
      .select('*', { count: 'exact', head: true })
      .eq('template_id', templateId)
      .eq('status', 'not_started');

    if (error) {
      throw error;
    }

    return count || 0;
  } catch (error) {
    throw error;
  }
}

/**
 * Propagate template item changes to all active (non-completed) assigned tasks.
 * Called after template items are modified in edit mode.
 * Uses the replace_assigned_task_items RPC so each task's delete+insert is atomic.
 * For recurring tasks, also resets occurrence item states for non-completed occurrences.
 * Returns the number of tasks that failed to update.
 */
export async function propagateTemplateItemsToActiveAssignments(templateId: string): Promise<{ failedCount: number }> {
  const { data: templateItems, error: itemsError } = await supabase
    .from('task_items')
    .select('*')
    .eq('template_id', templateId)
    .order('order_index', { ascending: true });

  if (itemsError) throw itemsError;
  if (!templateItems) return { failedCount: 0 };

  const { data: assignedTasks, error: tasksError } = await supabase
    .from('assigned_tasks')
    .select('id, is_recurring')
    .eq('template_id', templateId)
    .neq('status', 'completed');

  if (tasksError) throw tasksError;
  if (!assignedTasks || assignedTasks.length === 0) return { failedCount: 0 };

  const itemPayload = templateItems.map((item: any) => ({
    task_item_id: item.id,
    title: item.title,
    order_index: item.order_index,
    requires_text_input: item.requires_text_input ?? false,
    text_input_label: item.text_input_label ?? null,
    text_input_placeholder: item.text_input_placeholder ?? null,
    is_text_input_required: item.is_text_input_required ?? false,
    text_input_type: item.text_input_type ?? 'text',
    kpi_definition_id: item.kpi_definition_id ?? null,
  }));

  const results = await Promise.allSettled(
    assignedTasks.map(async (task) => {
      const { error: replaceError } = await supabase.rpc('replace_assigned_task_items', {
        p_assigned_task_id: task.id,
        p_items: itemPayload,
      });

      if (replaceError) throw replaceError;

      if (task.is_recurring) {
        const { error: occurrenceError } = await supabase
          .from('assigned_task_occurrences')
          .update({ notes: JSON.stringify({ itemStates: {} }) })
          .eq('assigned_task_id', task.id)
          .neq('status', 'completed');

        if (occurrenceError) throw occurrenceError;
      }
    })
  );

  const failedCount = results.filter(r => r.status === 'rejected').length;

  return { failedCount };
}
